import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    SettingType,
    CodeBlock,
    BlockType,
    Define,
    SettingState,
    SettingOption,
    ValidationMessage,
    ValidationMessageType,
    FeatureType,
} from '@appblocks/codegeneration/types';

export default class BoardSetting extends BaseFeature {
    constructor() {
        super();
        this.id = 'boardsetting';
        this.name = 'Board Settings';
        this.requirements = ['TPP'];
        this.priority = 9;
        const settings = [
            {
                id: 'POE',
                name: 'PoE Jumper Position',
                settingType: SettingType.HIDDEN,
                default: '0',
                required: true,
                readonly: true,
                options: [
                    {
                        name: 'Off',
                        value: '0',
                    },
                    {
                        name: 'On',
                        value: '1',
                    },
                ],
            },
            {
                id: '5VDIRECT',
                name: '5V Direct',
                settingType: SettingType.OPTION,
                default: '0',
                required: true,
                options: [
                    {
                        name: 'Off',
                        value: '0',
                    },
                    {
                        name: 'On',
                        value: '1',
                    },
                ],
            },
            {
                id: 'WIFIANTENNA',
                name: 'External Wifi Antenna Kit',
                settingType: SettingType.OPTION,
                default: '0',
                required: true,
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Include',
                        value: '1',
                    },
                ],
            },
            {
                id: 'GPRSANTENNA',
                name: 'External Cellular Antenna Kit',
                settingType: SettingType.OPTION,
                default: '0',
                required: true,
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Include',
                        value: '1',
                    },
                ],
            },
            {
                id: 'TPB',
                name: 'Tibbo Project Box',
                settingType: SettingType.OPTION,
                default: 'true',
                required: true,
                options: [
                    {
                        name: 'None',
                        value: 'false',
                    },
                    {
                        name: 'Include',
                        value: 'true',
                    },
                ],
            },
            {
                id: 'VPK',
                name: 'Vibration Protection Kit',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Include',
                        value: '1',
                    },
                ],
            },
            {
                id: 'DIN',
                name: 'DIN Rail Mounting Kit',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Include',
                        value: '1',
                    },
                ],
            },
            {
                id: 'POWERADAPTER',
                name: 'Power Adapter',
                settingType: SettingType.OPTION,
                default: 'apr-p0008',
                required: true,
                readonly: true,
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: '12V/1A US plug AC adapter',
                        value: 'apr-p0008',
                    },
                    {
                        name: '12V/1A EU plug AC adapter',
                        value: 'apr-p0009',
                    },
                    {
                        name: '12V/1A UK plug AC adapter',
                        value: 'apr-p0010',
                    },
                ],
            },
            {
                id: 'ASM',
                name: 'Packaging',
                settingType: SettingType.OPTION,
                default: 'asm+rp',
                required: true,
                options: [
                    {
                        name: '---',
                        value: '',
                    },
                    {
                        name: 'Add Retail Packaging',
                        value: 'rp',
                    },
                    {
                        name: 'Assemble and add Retail Packaging',
                        value: 'asm+rp',
                    },
                ],
            },
            {
                id: 'NOTES',
                name: 'Additional Notes',
                settingType: SettingType.STRING,
                default: '',
                required: false,
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getState(settingId: string): SettingState {
        const project: ProjectConfiguration = this.project;
        switch (settingId) {
            case 'POE':
            case '5VDIRECT':
            case 'GPRSANTENNA':
            case 'WIFIANTENNA':
                if (project.device.id === TIBBO_DEVICE_ID.LTPP3G2) {
                    return SettingState.HIDDEN;
                }
                return SettingState.ENABLED;
            case 'VPK':
            case 'DIN':
            case 'ASM':
                return (this.getSettingValue('TPB') === '0' || this.getSettingValue('TPB') === 'false') ? SettingState.HIDDEN : SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        if (settingId === 'TPB') {
            switch (project.device.id) {
                case TIBBO_DEVICE_ID.TPP2WG2:
                case 'TPP2W(G3)':
                    return [{
                        name: 'None',
                        value: 'false',
                    },
                    {
                        name: 'TPB2',
                        value: 'true',
                    },
                    {
                        name: 'TPB2L(+LCD and keypad)',
                        value: 'tpb2lg2',
                    }];
                default:
                    return [{
                        name: 'None',
                        value: 'false',
                    },
                    {
                        name: 'Include',
                        value: 'true',
                    }];
            }
        }
        return super.getSettingOptions(settingId);
    }

    calculatePowerBalance() {
        const model: { [socket: string]: string } = {};
        const { features } = this.project;
        for (let i = 0; i < features.length; i += 1) {
            if (features[i].featureType === FeatureType.TIBBIT) {
                model[features[i].getSettingValue('SOCKET')] = features[i].getSettingValue('TIBBIT');
            }
            if (features[i].id === 'wireless_module'
                && features[i].getSettingValue('MODULE') !== ''
                && features[i].getSettingValue('ENABLED') === '1'
            ) {
                model.wifi = features[i].getSettingValue('MODULE');
            }
        }
        const total: any = {
            '5V': { supply: 0, demand: 140 },
            '+15V': { supply: 0, demand: 0 },
            '-15V': { supply: 0, demand: 0 },
        };
        let boardSettings;
        for (let i = 0; i < features.length; i += 1) {
            if (features[i].id === 'boardsetting') {
                boardSettings = features[i];
            }
        }
        if (boardSettings !== undefined) {
            if (this.project.device.deviceType === 'TPP' && boardSettings.getSettingValue('5VDIRECT') === '1') {
                total['5V'].supply += 1000;
            }
            const lcdFeature = this.project.getFeature('lcd');
            const lcdEnabled = lcdFeature && lcdFeature.getEnabled();
            if (this.project.getSettingValue('boardsetting', 'TPB') === 'tpb2lg2' && lcdEnabled) {
                total['5V'].demand += 163;
            }
        }


        Object.keys(model).forEach((key) => {
            FeatureUtils.tibbit_form_factors[model[key]].power.forEach((item: {
                voltage: string | number;
                supply: any; value: string;
            }) => {
                if (total[item.voltage] === undefined) {
                    total[item.voltage] = { supply: 0, demand: 0 };
                }

                total[item.voltage][item.supply ? 'supply' : 'demand'] += parseInt(item.value, 10);
            });
        });

        return total;
    }

    validate(): Array<ValidationMessage> {
        const TPB = this.getSettingValue('TPB');
        const VPK = this.getSettingValue('VPK');
        const DIN = this.getSettingValue('DIN');
        const errors = [];
        if (TPB === 'false') {
            if (VPK !== '0') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'VPK',
                    data: '0',
                });
            }
            if (DIN !== '0') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'DIN',
                    data: '0',
                });
            }
        }

        if (this.project.device.deviceType === 'TPP') {
            const powerConsumption = this.calculatePowerBalance();

            const powerKeys = Object.keys(powerConsumption);
            for (let i = 0; i < powerKeys.length; i += 1) {
                const key = powerKeys[i];
                if (powerConsumption[key].demand > 0) {
                    if (powerConsumption[key].demand > powerConsumption[key].supply) {
                        const boardSettings = this.project.getFeature('boardsetting');
                        let tibbitSuggestion = '';
                        if (key === '5V') {
                            tibbitSuggestion = 'Add a Tibbit #10 or Tibbit #33';
                        } else if (key === '+15V' || key === '-15V') {
                            tibbitSuggestion = 'Add a Tibbit #12';
                        }
                        errors.push({
                            featureId: boardSettings.getID(),
                            message:
                                `Board Error: Not enough power for ${key}.
${tibbitSuggestion}`,
                            messageType: ValidationMessageType.ERROR,
                        });
                    }
                }
            }
        }

        return errors;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];
        // LCD enabled
        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/phm/tibbo_project_system';
    }
}
